// @ts-strict-ignore
import React, { ReactElement, useMemo } from 'react'
import dayjs from 'dayjs'
import { TFunction, useTranslation } from 'react-i18next'
import {
  ActionList,
  ActionListChildren,
  Avatar,
  Box,
  Button,
  Highlight,
  Icon,
  useSidePanel,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ContactCase } from 'types/contacts'
import { PaymentContextType } from 'types/payments'
import { formatCurrencyAmount } from 'utils/utils'
import { ContactCaseListIcon } from './components/ContactCaseListIcon'
import { ContactCaseSummary } from './components/ContactCaseSummary'

interface ContactCaseListProps {
  contactCases: ContactCase[]
  handleAssignContactClick: (contactCase: ContactCase) => void
}

const getHighlightDescription = (
  paymentContextType: PaymentContextType,
  t: TFunction<'translation', undefined>
) => {
  switch (paymentContextType) {
    case PaymentContextType.CHANNEL_PAYMENT:
      return t('contacts.channelPayment')
    case PaymentContextType.PAYMENT_IN:
      return t('contacts.cryptoPayment')
    case PaymentContextType.DEPOSIT:
      return t('contacts.directDeposit')
    case PaymentContextType.WITHDRAWAL:
      return t('contacts.withdrawal')
    default:
      return
  }
}

export const ContactCaseList = ({
  contactCases,
  handleAssignContactClick,
}: ContactCaseListProps) => {
  const { t } = useTranslation()
  const { enableWalletVerification } = useFeatureFlags()

  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.payments
  const canEdit = permissions?.canEdit

  const {
    isSidePanelOpen: isContactCaseDetailsOpen,
    sidePanelData: contactCase,
    paging: pageContactCases,
    setIsSidePanelOpen: setIsContactCaseDetailsOpen,
    updateSidePanel: updateContactCaseDetails,
  } = useSidePanel<ContactCase>(contactCases)

  const onDetailsClick = (contactCaseIndex: number) => {
    updateContactCaseDetails(contactCaseIndex)
    setIsContactCaseDetailsOpen(true)
  }

  const actionList: ActionListChildren[] = useMemo(() => {
    return contactCases?.map((contactCase, contactCaseIndex) => {
      const {
        externalId,
        amount,
        currencyCode,
        network,
        paymentCreated,
        paymentContext: { type: paymentContextType } = {},
        requiredInformation = [],
      } = contactCase || {}

      const title = formatCurrencyAmount(amount, currencyCode, network)

      const headerHighlightLeft = (
        <>
          {' '}
          <Highlight
            iconElement={<ContactCaseListIcon paymentContextType={paymentContextType} />}
            type="feature"
            variant="emphasis"
          >
            {getHighlightDescription(paymentContextType, t)}
          </Highlight>
        </>
      )

      let ActionRequired: ReactElement = undefined
      if (!enableWalletVerification || requiredInformation.includes('CONTACT_REQUIRED')) {
        // CONTACT_REQUIRED action needs to be tackled before INSTRUMENT_VERIFICATION_REQUIRED can be acted upon
        ActionRequired = (
          <Button
            disabled={!canEdit || viewOnly}
            iconElement={<Icon name="UserCirclePlus" />}
            variant="ghost"
            onClick={() => handleAssignContactClick(contactCase)}
          >
            {t('contacts.assignContact')}
          </Button>
        )
      } else if (requiredInformation.includes('INSTRUMENT_VERIFICATION_REQUIRED')) {
        ActionRequired = (
          <Button
            testId="verify-wallet-link"
            iconElement={<Icon name="ArrowSquareOut" />}
            variant="ghost"
            onClick={() =>
              globalThis.open(
                `${process.env.PUBLIC_WALLET_VERIFY_URL}/${externalId}`,
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            {t('contacts.verifyWallet')}
          </Button>
        )
      }
      return {
        id: externalId,
        key: externalId,
        title,
        transparent: true,
        HeaderHighlightLeft: headerHighlightLeft,
        Avatar: <Avatar type="currency" name={currencyCode ? currencyCode?.toLowerCase() : ''} />,
        BodyPre: dayjs(paymentCreated).format(DateFormat.DD_MMM_YYYY_HH_MM_A),
        CustomAction: ActionRequired,
        border: true,
        onItemClick: () => onDetailsClick(contactCaseIndex),
      }
    })
  }, [contactCases])
  return (
    <Box>
      <Box paddingB={16}>
        <ActionList id="contact-case-list-action-list" items={actionList} />
      </Box>

      <ContactCaseSummary
        isOpen={isContactCaseDetailsOpen}
        contactCase={contactCase}
        pageBeneficiaries={pageContactCases}
        onClose={() => setIsContactCaseDetailsOpen(false)}
      />
    </Box>
  )
}
